import { useEffect, useState } from "react";
import { getJobLocationTimeUpdates } from "../../../api/missioncontrolApi";
import LoadingSpinner from "../../LoadingSpinner";
import { formatFromJsonDate } from "../../../dateConfig";
import { useSelector } from "react-redux";
import { ChevronLeft, ChevronRight } from "react-feather";

const LocationPopUp = ({ jobId, locId, unvisitedIndex, unvisited, key }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [locationData, setLocationData] = useState({});
  const [visits, setVisits] = useState({});
  const [currentIndex, setCurrentIndex] = useState(null);
  const [multipleVisits, setMultipleVisits] = useState(false);
  const [calculatedArrival, setCalculatedArrival] = useState(null);
  const [calculatedDeparture, setCalculatedDeparture] = useState(null);
  const [calculatedIsOnTimeArrival, setCalculatedIsOnTimeArrival] =
    useState(null);
  const [calculatedIsOnTimeDeparture, setCalculatedIsOnTimeDeparture] =
    useState(null);

  const unvisitedDirectionData = useSelector(
    (state) => state.missionControl.unvisitedDirections,
  );

  useEffect(() => {
    if (unvisited && locationData) {
      const calculatedDuration = unvisitedDirectionData
        .slice(0, unvisitedIndex)
        .reduce((acc, leg) => acc + leg.duration.value, 0);
      const unformattedArrival = calculatedDuration * 1000 + Date.now();
      setCalculatedArrival(
        calculatedDuration ? formatFromJsonDate(unformattedArrival) : "N/A",
      );
      const arrivalDepartureDifference =
        locationData.rlkDepartureEta - locationData.rlkArrivalEta;

      const unformattedDeparture =
        unformattedArrival + arrivalDepartureDifference;
      setCalculatedDeparture(
        calculatedDuration ? formatFromJsonDate(unformattedDeparture) : "N/A",
      );
      setCalculatedIsOnTimeArrival(
        calculatedDuration
          ? locationData.rlkArrivalEta >= unformattedArrival
            ? "Early"
            : "Late"
          : null,
      );
      setCalculatedIsOnTimeDeparture(
        calculatedDuration
          ? locationData.rlkDepartureEta >= unformattedDeparture
            ? "Early"
            : "Late"
          : null,
      );
    }
  }, [locationData, unvisited, unvisitedDirectionData, unvisitedIndex]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const locationDetails = await getJobLocationTimeUpdates(jobId, locId);
      if (locationDetails.length > 1) {
        setVisits(
          locationDetails.reduce((obj, item) => {
            obj[item.planedSeq] = item;
            return obj;
          }, {}),
        );
        setMultipleVisits(true);
        setLocationData(locationDetails[0]);
        setCurrentIndex(0);
      } else {
        setMultipleVisits(false);
        setLocationData(locationDetails[0]);
      }
      setIsLoading(false);
    })();
  }, [jobId, locId]);

  useEffect(() => {
    const currSeq = Object.keys(visits)[currentIndex];
    setLocationData(visits[currSeq]);
  }, [currentIndex, visits]);

  const contentStyle = { minWidth: "180px", margin: 0 };

  const labelStyle = {
    minWidth: "160px",
    textAlign: "start",
    margin: 0,
  };

  const colStyle = {
    display: "flex",
    gap: "10px",
    margin: 0,
  };

  const separatorStyle = {
    margin: 0,
  };

  return (
    <>
      {isLoading ? (
        <LoadingSpinner height="320px" width="320px" />
      ) : (
        <div
          key={key}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "10px",
          }}
        >
          {multipleVisits && (
            <ChevronLeft
              color={currentIndex === 0 ? "#D3D3D3" : "black"}
              style={{ cursor: currentIndex !== 0 ? "pointer" : "default" }}
              onClick={() => {
                if (currentIndex !== 0) {
                  setCurrentIndex((prevState) => --prevState);
                }
              }}
            />
          )}
          <div
            className="mission-control-map-popup"
            style={{
              display: "flex",
              gap: "10px",
              padding: "10px",
              flexDirection: "column",
            }}
          >
            <div className="ui dividing header">{locationData.locName}</div>
            <div style={colStyle}>
              <div style={labelStyle}>Arrival ETA</div>
              <div style={separatorStyle}>:</div>
              <div style={contentStyle}>
                <div
                  className={`ui horizontal label`}
                  style={{
                    backgroundColor:
                      locationData.rlkLocationType === 1
                        ? "#1E90FF"
                        : locationData.rlkLocationType === 2
                          ? "#32CD32"
                          : "#FFCC33",
                    color: "white",
                  }}
                >
                  {locationData.locationTypeName}
                </div>
              </div>
            </div>
            <div style={colStyle}>
              <div style={labelStyle}>Arrival ETA</div>
              <div style={separatorStyle}>:</div>
              <h4 style={contentStyle}>
                {locationData.rlkArrivalEta
                  ? formatFromJsonDate(locationData.rlkArrivalEta)
                  : "N/A"}
              </h4>
            </div>
            <div style={colStyle}>
              <div style={labelStyle}>
                {unvisited ? "Calculated Arrival" : "Arrival Actual"}
              </div>
              <div style={separatorStyle}>:</div>
              <h4 style={contentStyle}>
                {unvisited
                  ? calculatedArrival
                  : locationData.rtuArrivalTime
                    ? formatFromJsonDate(locationData.rtuArrivalTime)
                    : "N/A"}
              </h4>
            </div>
            <div style={colStyle}>
              <div style={labelStyle}>Arrival On time</div>
              <div style={separatorStyle}>:</div>
              <div style={contentStyle}>
                {unvisited ? (
                  calculatedIsOnTimeArrival ? (
                    <div
                      className={`ui ${calculatedIsOnTimeArrival === "Early" ? "green" : "red"} horizontal label`}
                    >
                      {calculatedIsOnTimeArrival}
                    </div>
                  ) : (
                    "N/A"
                  )
                ) : locationData.ontimeArrivalStatus ? (
                  <div
                    className={`ui ${locationData.ontimeArrivalStatus === "Early" ? "green" : "red"} horizontal label`}
                  >
                    {locationData.ontimeArrivalStatus}
                  </div>
                ) : (
                  "N/A"
                )}
              </div>
            </div>
            <div style={colStyle}>
              <div style={labelStyle}>Departure ETA</div>
              <div style={separatorStyle}>:</div>
              <h4 style={contentStyle}>
                {locationData.rlkDepartureEta
                  ? formatFromJsonDate(locationData.rlkDepartureEta)
                  : "N/A"}
              </h4>
            </div>
            <div style={colStyle}>
              <div style={labelStyle}>
                {unvisited ? "Calculated Departure" : "Departure Actual"}
              </div>
              <div style={separatorStyle}>:</div>
              <h4 style={contentStyle}>
                {unvisited
                  ? calculatedDeparture
                  : locationData.rtuDepartureTime
                    ? formatFromJsonDate(locationData.rtuDepartureTime)
                    : "N/A"}
              </h4>
            </div>
            <div style={colStyle}>
              <div style={labelStyle}>Departure On time</div>
              <div style={separatorStyle}>:</div>
              <div style={contentStyle}>
                {unvisited ? (
                  calculatedIsOnTimeDeparture ? (
                    <div
                      className={`ui ${calculatedIsOnTimeArrival === "Early" ? "green" : "red"} horizontal label`}
                    >
                      {calculatedIsOnTimeArrival}
                    </div>
                  ) : (
                    "N/A"
                  )
                ) : locationData.ontimeDepartureStatus ? (
                  <div
                    className={`ui ${locationData.ontimeDepartureStatus === "Early" ? "green" : "red"} horizontal label`}
                  >
                    {locationData.ontimeDepartureStatus}
                  </div>
                ) : (
                  "N/A"
                )}
              </div>
            </div>
          </div>
          {multipleVisits && (
            <ChevronRight
              color={
                currentIndex === Object.keys(visits).length - 1
                  ? "#D3D3D3"
                  : "black"
              }
              style={{
                cursor:
                  currentIndex !== Object.keys(visits).length - 1
                    ? "pointer"
                    : "default",
              }}
              onClick={() => {
                if (currentIndex !== Object.keys(visits).length - 1) {
                  setCurrentIndex((prevState) => ++prevState);
                }
              }}
            />
          )}
        </div>
      )}
    </>
  );
};

export default LocationPopUp;
